import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../lib/auth';
import api from '../../lib/api';
import { LogOut, FileText, Image, Briefcase, Mail, Trash2, Plus, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_creative-ai-studio-151/artifacts/0wsobm4i_zsdigitals%20logo.jpeg';

const TAB_NAMES = ['blogs', 'portfolio', 'cases', 'jobs', 'contacts', 'newsletters', 'applications'];
const BLOG_COLUMNS = ['title', 'category', 'featured'];
const PORTFOLIO_COLUMNS = ['title', 'category'];
const CASE_COLUMNS = ['title', 'client_name'];
const JOB_COLUMNS = ['title', 'department', 'location'];
const CONTACT_COLUMNS = ['name', 'email', 'service_required', 'message'];
const NEWSLETTER_COLUMNS = ['email', 'created_at'];
const APPLICATION_COLUMNS = ['name', 'email', 'position'];

const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [applications, setApplications] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [b, p, cs, j, c, n, a] = await Promise.all([
        api.getBlogs(), api.getPortfolio(), api.getCaseStudies(), api.getJobs(),
        api.getContacts(), api.getNewsletters(), api.getApplications(),
      ]);
      setBlogs(b.data); setPortfolios(p.data); setCaseStudies(cs.data); setJobs(j.data);
      setContacts(c.data); setNewsletters(n.data); setApplications(a.data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch admin data:', err);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) return;
    if (user) fetchAll();
  }, [user, loading, fetchAll]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => { await logout(); navigate('/admin'); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#050508]"><div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050508]" data-testid="admin-login">
        <motion.div {...fadeIn} className="text-center p-10 rounded-3xl bg-white/[0.02] border border-white/[0.06] max-w-md w-full mx-4">
          <img src={LOGO_URL} alt="ZS Digitals" className="h-16 w-16 mx-auto rounded-2xl object-contain mb-6" />
          <h2 className="font-poppins font-bold text-2xl text-white mb-2">Admin Panel</h2>
          <p className="text-[#A1A1AA] text-sm mb-8">Sign in with Google to manage your website content.</p>
          <button onClick={handleLogin} data-testid="admin-google-login" className="w-full px-6 py-3 bg-[#7C3AED] text-white font-semibold rounded-full hover:bg-[#6D28D9] hover:shadow-[0_8px_25px_rgba(124,58,237,0.4)] transition-all duration-300">
            Sign in with Google
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508] pt-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <div><h1 className="font-poppins font-bold text-2xl text-white">Dashboard</h1><p className="text-[#A1A1AA] text-sm">Welcome back, {user.name}</p></div>
          <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 px-4 py-2 text-sm text-[#A1A1AA] hover:text-[#7C3AED] transition-colors"><LogOut className="w-4 h-4" /> Logout</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Blog Posts', count: blogs.length, icon: FileText, color: '#7C3AED' },
            { label: 'Portfolio', count: portfolios.length, icon: Image, color: '#06B6D4' },
            { label: 'Contacts', count: contacts.length, icon: Mail, color: '#7C3AED' },
            { label: 'Applications', count: applications.length, icon: Briefcase, color: '#06B6D4' },
          ].map(stat => (
            <div key={stat.label} className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <stat.icon className="w-5 h-5 mb-2" style={{ color: stat.color }} />
              <div className="font-poppins font-bold text-2xl text-white">{stat.count}</div>
              <div className="text-[#71717A] text-xs">{stat.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="blogs" className="mb-12">
          <TabsList className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-1 mb-6 flex-wrap h-auto">
            {TAB_NAMES.map(t => (
              <TabsTrigger key={t} value={t} className="rounded-lg text-xs" data-testid={`tab-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="blogs">
            <CreateBlogForm onCreated={fetchAll} />
            <AdminTable items={blogs} columns={BLOG_COLUMNS} onDelete={async (id) => { await api.deleteBlog(id); fetchAll(); toast.success('Deleted'); }} testId="blogs" />
          </TabsContent>
          <TabsContent value="portfolio">
            <CreatePortfolioForm onCreated={fetchAll} />
            <AdminTable items={portfolios} columns={PORTFOLIO_COLUMNS} onDelete={async (id) => { await api.deletePortfolio(id); fetchAll(); toast.success('Deleted'); }} testId="portfolio" />
          </TabsContent>
          <TabsContent value="cases">
            <CreateCaseStudyForm onCreated={fetchAll} />
            <AdminTable items={caseStudies} columns={CASE_COLUMNS} onDelete={async (id) => { await api.deleteCaseStudy(id); fetchAll(); toast.success('Deleted'); }} testId="cases" />
          </TabsContent>
          <TabsContent value="jobs">
            <CreateJobForm onCreated={fetchAll} />
            <AdminTable items={jobs} columns={JOB_COLUMNS} onDelete={async (id) => { await api.deleteJob(id); fetchAll(); toast.success('Deleted'); }} testId="jobs" />
          </TabsContent>
          <TabsContent value="contacts"><AdminTable items={contacts} columns={CONTACT_COLUMNS} testId="contacts" /></TabsContent>
          <TabsContent value="newsletters"><AdminTable items={newsletters} columns={NEWSLETTER_COLUMNS} testId="newsletters" /></TabsContent>
          <TabsContent value="applications"><AdminTable items={applications} columns={APPLICATION_COLUMNS} testId="applications" /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ========== CRUD FORMS ========== */

function CreateBlogForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', category: '', image: '', featured: false });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.createBlog(form); toast.success('Blog post created!'); setOpen(false); setForm({ title: '', excerpt: '', content: '', category: '', image: '', featured: false }); onCreated(); }
    catch { toast.error('Error creating blog post'); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="mb-4 px-5 py-2.5 bg-[#7C3AED] text-white text-sm font-semibold rounded-full hover:bg-[#6D28D9] transition-all flex items-center gap-2" data-testid="create-blog-btn"><Plus className="w-4 h-4" /> New Blog Post</button></DialogTrigger>
      <DialogContent className="bg-[#12121C] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-poppins text-white">Create Blog Post</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label className="text-white text-sm mb-1 block">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-white/5 border-white/10 text-white" required data-testid="blog-form-title" /></div>
          <div><Label className="text-white text-sm mb-1 block">Category</Label>
            <Select value={form.category} onValueChange={val => setForm({...form, category: val})}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="blog-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{['AI & Technology', 'Marketing Strategy', 'Paid Advertising', 'Organic Growth'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-white text-sm mb-1 block">Excerpt</Label><Textarea value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} className="bg-white/5 border-white/10 text-white min-h-[60px]" required data-testid="blog-form-excerpt" /></div>
          <div><Label className="text-white text-sm mb-1 block">Content</Label><Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="bg-white/5 border-white/10 text-white min-h-[120px]" required data-testid="blog-form-content" /></div>
          <div><Label className="text-white text-sm mb-1 block">Image URL</Label><Input value={form.image} onChange={e => setForm({...form, image: e.target.value})} className="bg-white/5 border-white/10 text-white" data-testid="blog-form-image" /></div>
          <div className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="rounded" data-testid="blog-form-featured" /><Label className="text-white text-sm">Featured post</Label></div>
          <button type="submit" disabled={submitting} className="w-full py-3 bg-[#7C3AED] text-white rounded-full font-semibold hover:bg-[#6D28D9] transition-all disabled:opacity-50" data-testid="blog-form-submit">{submitting ? 'Creating...' : 'Create Post'}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreatePortfolioForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: '', image: '', tags: [] });
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const addTag = () => { if (tagInput.trim()) { setForm({...form, tags: [...form.tags, tagInput.trim()]}); setTagInput(''); } };
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.createPortfolio(form); toast.success('Portfolio item created!'); setOpen(false); setForm({ title: '', description: '', category: '', image: '', tags: [] }); onCreated(); }
    catch { toast.error('Error'); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="mb-4 px-5 py-2.5 bg-[#7C3AED] text-white text-sm font-semibold rounded-full hover:bg-[#6D28D9] transition-all flex items-center gap-2" data-testid="create-portfolio-btn"><Plus className="w-4 h-4" /> New Portfolio Item</button></DialogTrigger>
      <DialogContent className="bg-[#12121C] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-poppins text-white">Create Portfolio Item</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label className="text-white text-sm mb-1 block">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-white/5 border-white/10 text-white" required data-testid="portfolio-form-title" /></div>
          <div><Label className="text-white text-sm mb-1 block">Category</Label>
            <Select value={form.category} onValueChange={val => setForm({...form, category: val})}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{['AI Video', 'Paid Ads', 'UGC', 'Design'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-white text-sm mb-1 block">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-white/5 border-white/10 text-white min-h-[80px]" required /></div>
          <div><Label className="text-white text-sm mb-1 block">Image URL</Label><Input value={form.image} onChange={e => setForm({...form, image: e.target.value})} className="bg-white/5 border-white/10 text-white" /></div>
          <div>
            <Label className="text-white text-sm mb-1 block">Tags</Label>
            <div className="flex gap-2"><Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} className="bg-white/5 border-white/10 text-white" placeholder="Add tag" /><button type="button" onClick={addTag} className="px-3 py-2 bg-[#7C3AED]/20 text-[#7C3AED] rounded-lg text-sm">Add</button></div>
            <div className="flex flex-wrap gap-1 mt-2">{form.tags.map((t,i) => <span key={`tag-${t}-${i}`} className="px-2 py-0.5 text-xs rounded-full bg-[#7C3AED]/10 text-[#A855F7] flex items-center gap-1">{t}<button type="button" onClick={() => setForm({...form, tags: form.tags.filter((_,j) => j !== i)})} className="hover:text-white"><X className="w-3 h-3" /></button></span>)}</div>
          </div>
          <button type="submit" disabled={submitting} className="w-full py-3 bg-[#7C3AED] text-white rounded-full font-semibold hover:bg-[#6D28D9] transition-all disabled:opacity-50">{submitting ? 'Creating...' : 'Create Item'}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateCaseStudyForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', client_name: '', challenge: '', solution: '', result: '', image: '', metrics: [] });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.createCaseStudy(form); toast.success('Case study created!'); setOpen(false); setForm({ title: '', client_name: '', challenge: '', solution: '', result: '', image: '', metrics: [] }); onCreated(); }
    catch { toast.error('Error'); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="mb-4 px-5 py-2.5 bg-[#7C3AED] text-white text-sm font-semibold rounded-full hover:bg-[#6D28D9] transition-all flex items-center gap-2" data-testid="create-case-study-btn"><Plus className="w-4 h-4" /> New Case Study</button></DialogTrigger>
      <DialogContent className="bg-[#12121C] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-poppins text-white">Create Case Study</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label className="text-white text-sm mb-1 block">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-white/5 border-white/10 text-white" required /></div>
          <div><Label className="text-white text-sm mb-1 block">Client Name</Label><Input value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="bg-white/5 border-white/10 text-white" required /></div>
          <div><Label className="text-white text-sm mb-1 block">Challenge</Label><Textarea value={form.challenge} onChange={e => setForm({...form, challenge: e.target.value})} className="bg-white/5 border-white/10 text-white min-h-[60px]" required /></div>
          <div><Label className="text-white text-sm mb-1 block">Solution</Label><Textarea value={form.solution} onChange={e => setForm({...form, solution: e.target.value})} className="bg-white/5 border-white/10 text-white min-h-[60px]" required /></div>
          <div><Label className="text-white text-sm mb-1 block">Result</Label><Textarea value={form.result} onChange={e => setForm({...form, result: e.target.value})} className="bg-white/5 border-white/10 text-white min-h-[60px]" required /></div>
          <div><Label className="text-white text-sm mb-1 block">Image URL</Label><Input value={form.image} onChange={e => setForm({...form, image: e.target.value})} className="bg-white/5 border-white/10 text-white" /></div>
          <button type="submit" disabled={submitting} className="w-full py-3 bg-[#7C3AED] text-white rounded-full font-semibold hover:bg-[#6D28D9] transition-all disabled:opacity-50">{submitting ? 'Creating...' : 'Create Case Study'}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateJobForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', department: '', location: '', type: 'Full-time', description: '', requirements: [] });
  const [reqInput, setReqInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const addReq = () => { if (reqInput.trim()) { setForm({...form, requirements: [...form.requirements, reqInput.trim()]}); setReqInput(''); } };
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.createJob(form); toast.success('Job created!'); setOpen(false); setForm({ title: '', department: '', location: '', type: 'Full-time', description: '', requirements: [] }); onCreated(); }
    catch { toast.error('Error'); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="mb-4 px-5 py-2.5 bg-[#7C3AED] text-white text-sm font-semibold rounded-full hover:bg-[#6D28D9] transition-all flex items-center gap-2" data-testid="create-job-btn"><Plus className="w-4 h-4" /> New Job</button></DialogTrigger>
      <DialogContent className="bg-[#12121C] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-poppins text-white">Create Job Listing</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label className="text-white text-sm mb-1 block">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-white/5 border-white/10 text-white" required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-white text-sm mb-1 block">Department</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="bg-white/5 border-white/10 text-white" required /></div>
            <div><Label className="text-white text-sm mb-1 block">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-white/5 border-white/10 text-white" required /></div>
          </div>
          <div><Label className="text-white text-sm mb-1 block">Type</Label>
            <Select value={form.type} onValueChange={val => setForm({...form, type: val})}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{['Full-time', 'Part-time', 'Contract', 'Remote'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-white text-sm mb-1 block">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-white/5 border-white/10 text-white min-h-[80px]" required /></div>
          <div>
            <Label className="text-white text-sm mb-1 block">Requirements</Label>
            <div className="flex gap-2"><Input value={reqInput} onChange={e => setReqInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addReq())} className="bg-white/5 border-white/10 text-white" placeholder="Add requirement" /><button type="button" onClick={addReq} className="px-3 py-2 bg-[#7C3AED]/20 text-[#7C3AED] rounded-lg text-sm">Add</button></div>
            <ul className="mt-2 space-y-1">{form.requirements.map((r,i) => <li key={`req-${r.slice(0,10)}-${i}`} className="flex items-center gap-2 text-[#A1A1AA] text-sm"><div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />{r}<button type="button" onClick={() => setForm({...form, requirements: form.requirements.filter((_,j) => j !== i)})} className="text-[#71717A] hover:text-white ml-auto"><X className="w-3 h-3" /></button></li>)}</ul>
          </div>
          <button type="submit" disabled={submitting} className="w-full py-3 bg-[#7C3AED] text-white rounded-full font-semibold hover:bg-[#6D28D9] transition-all disabled:opacity-50">{submitting ? 'Creating...' : 'Create Job'}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ========== TABLE ========== */

function formatCellValue(value) {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value || '-');
}

function AdminTable({ items, columns, onDelete, testId }) {
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden" data-testid={`admin-table-${testId}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.06] bg-white/[0.02]">
              {columns.map(col => <th key={col} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#71717A]">{col.replace(/_/g, ' ')}</th>)}
              {onDelete && <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#71717A]">Action</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={columns.length + (onDelete ? 1 : 0)} className="px-4 py-8 text-center text-[#71717A] text-sm">No items yet</td></tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                  {columns.map(col => <td key={col} className="px-4 py-3 text-sm text-[#A1A1AA] max-w-xs truncate">{formatCellValue(item[col])}</td>)}
                  {onDelete && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => onDelete(item.id)} className="p-1.5 rounded-lg text-[#71717A] hover:text-red-400 hover:bg-red-400/10 transition-all" data-testid={`delete-${testId}-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
